// @ts-nocheck
import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import { listConversations } from 'shared/api/services/LastUserCompanyService';
import { POLLING_INTERVALS } from 'shared/config/polling';
import { useVisiblePolling } from 'shared/hooks/useVisiblePolling';
import { NextStepContent } from 'shared/ui/NextStep';
import { useI18n } from './I18nContext';

const STORAGE_KEY = "messages_last_visited";
// F1-16: 30 sn → 120 sn. Bu context HER SAYFADA aktif oldugu icin
// tek basina en yaygin polling kaynagiydi (rapor §11.5).
const POLL_INTERVAL = POLLING_INTERVALS.messages;

const getCurrentUserId = () => {
  try {
    const stored = localStorage.getItem("userDetails");
    const parsed = stored ? JSON.parse(stored) : null;
    return parsed?.id || parsed?.user?.id || parsed?.userId || "";
  } catch {
    return "";
  }
};

const getLastVisited = () => {
  try {
    const val = localStorage.getItem(STORAGE_KEY);
    return val ? new Date(val) : new Date(0);
  } catch {
    return new Date(0);
  }
};

const buildMessagesPath = (conversationIds) =>
  conversationIds.length === 1
    ? `/messages?conversationId=${encodeURIComponent(conversationIds[0])}`
    : "/messages";

const NewMessageToast = ({ to, closeToast }) => {
  const { t } = useI18n();
  return (
    <NextStepContent
      message={t("nextStep.newMessage")}
      action={{ to, label: t("nextStep.goToMessages") }}
      closeToast={closeToast}
    />
  );
};

const MessageNotificationContext = createContext({ unreadCount: 0, clearUnread: () => {} });

export const MessageNotificationProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const prevIdsRef = useRef(new Set());

  const fetchAndCheck = useCallback(async () => {
    try {
      const res = await listConversations();
      const data = Array.isArray(res?.data) ? res.data : res?.data?.data || [];
      const currentUserId = getCurrentUserId();
      const lastVisited = getLastVisited();

      let count = 0;
      const newIds = new Set();

      data.forEach((conv) => {
        const lastMsg = conv.lastMessage;
        if (!lastMsg) return;
        const msgDate = new Date(lastMsg.createdAt);
        const isFromOther = lastMsg.senderUserId !== currentUserId;
        if (isFromOther && msgDate > lastVisited) {
          count++;
          newIds.add(conv.id);
        }
      });

      const reallyNew = [...newIds].filter((id) => !prevIdsRef.current.has(id));
      if (reallyNew.length > 0 && prevIdsRef.current.size > 0) {
        const to = buildMessagesPath(reallyNew);
        toast.info(({ closeToast }) => <NewMessageToast to={to} closeToast={closeToast} />, {
          toastId: "new-message-notification",
          position: "top-right",
          autoClose: 6000,
        });
      }

      prevIdsRef.current = newIds;
      setUnreadCount(count);
    } catch {
      // sessizce geç
    }
  }, []);

  // Ilk cagri: oturum varsa hemen bir kez.
  useEffect(() => {
    if (!localStorage.getItem("userDetails")) return;
    fetchAndCheck();
  }, [fetchAndCheck]);

  // F1-17: sekme gizliyken polling durur.
  useVisiblePolling(fetchAndCheck, POLL_INTERVAL, {
    enabled: Boolean(typeof window !== "undefined" && localStorage.getItem("userDetails")),
  });

  const clearUnread = useCallback(() => {
    localStorage.setItem(STORAGE_KEY, new Date().toISOString());
    setUnreadCount(0);
    prevIdsRef.current = new Set();
  }, []);

  return (
    <MessageNotificationContext.Provider value={{ unreadCount, clearUnread }}>
      {children}
    </MessageNotificationContext.Provider>
  );
};

export const useMessageNotification = () => useContext(MessageNotificationContext);
